/**
 * @module sign-out-curtain
 * @description Covers the authenticated UI the moment sign-out starts.
 */

/** Id of the injected node, so repeated calls can't stack curtains. */
const CURTAIN_ID = "taqat-signout-curtain";

/**
 * Resolves an opaque background colour for the curtain.
 *
 * Prefers whatever the document is actually painting right now, so the curtain
 * is invisible against the page it covers in either theme. Falls back to the
 * theme token (`--background` holds HSL channels, hence the `hsl()` wrapper),
 * which is defined on `:root` and overridden by `.dark`.
 */
function resolveBackground(): string {
  const fallback = "hsl(var(--background, 0 0% 100%))";

  try {
    for (const el of [document.body, document.documentElement]) {
      if (!el) continue;
      const color = getComputedStyle(el).backgroundColor;
      // Skip the transparent values ("transparent", "rgba(…, 0)") that a page
      // painting through to its ancestor reports.
      if (color && color !== "transparent" && !/,\s*0\)$/.test(color)) {
        return color;
      }
    }
  } catch {
    // getComputedStyle can throw in exotic embedding contexts — use the token.
  }

  return fallback;
}

/**
 * Paints an opaque full-viewport layer over the app.
 *
 * Sign-out ends in a full document load, and a browser keeps showing the
 * current document until the replacement commits — so on a slow connection the
 * user goes on staring at their own signed-in workspace for as long as that
 * takes. React can't be relied on to paint over it: the tree may be mid-unmount
 * and any state update queued here can lose the race with the navigation. A
 * directly appended node is painted on the next frame regardless of what React
 * is doing, and dies with the document.
 *
 * Idempotent, and a no-op outside the browser or before `<body>` exists.
 *
 * @param message - Text shown while the sign-in page loads
 */
export function showSignOutCurtain(message = "Signing out..."): void {
  if (typeof document === "undefined" || !document.body) return;
  if (document.getElementById(CURTAIN_ID)) return;

  const curtain = document.createElement("div");
  curtain.id = CURTAIN_ID;
  // Announce rather than trap: the document is about to be replaced, so there
  // is nothing here to focus.
  curtain.setAttribute("role", "status");
  curtain.setAttribute("aria-live", "polite");
  // Layout classes are the app's own, so the curtain and the "Initializing
  // application..." screen that follows it on the next document are the same
  // picture — sign-out reads as one continuous load rather than two unrelated
  // screens. Positioning and background stay inline: they must hold even if a
  // stylesheet is still loading.
  curtain.className = "flex items-center justify-center";
  curtain.style.cssText = [
    "position:fixed",
    "inset:0",
    // Above every app layer, including Radix portals (which top out far lower).
    "z-index:2147483647",
    `background:${resolveBackground()}`,
    "display:flex",
    "align-items:center",
    "justify-content:center",
  ].join(";");

  // Same markup as the app's boot screen in app.tsx.
  const stack = document.createElement("div");
  stack.className = "flex flex-col items-center gap-4";

  const spinner = document.createElement("div");
  spinner.className = "animate-spin rounded-full h-8 w-8 border-b-2 border-primary";

  const label = document.createElement("p");
  label.className = "text-sm text-muted-foreground";
  label.textContent = message;

  stack.appendChild(spinner);
  stack.appendChild(label);
  curtain.appendChild(stack);

  document.body.appendChild(curtain);
}
